import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import dataStorage from 'misc/data-storage';

const propTypes = {
  children: PropTypes.any,
};

const PERMISSIONS = {
  '/':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/login':
    { UNKNOWN: true, USER: true, MANAGER: true, ADMIN: true },
  '/patients/add_patient':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/patients/patient_profile':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/patients/patients':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/patients/search_patients':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/patients/search_patients_vcs':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/add_caregiver':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/caregiver_profile':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/caregivers':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/search_caregivers':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/search_new_caregivers':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/search_expire_caregivers':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/caregiver_sick_leave_report':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/caregivers/search_efiles':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/visits/search_visits':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/visits/search_unbillable_visits':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/visits/confirm_visits':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/evv/search_evvs':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/evv/export_duty_sheet':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/invoices/create_invoices':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/invoices/create_admin_fee_invoices':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/invoices/search_invoices':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/invoices/create_ebillings':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/invoices/invoice_details':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/invoices/account_receivable':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/payments/add_payment':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/payments/search_payments':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/payments/payment_details':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/payments/upload_era':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/reports/caregiver_payrolls':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/reports/account_receivable':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/reports/search_expiring_auths':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
  '/reports/search_active_masterweeks':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/providers/care_bridge_visits':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/providers/hha_exchange_visits':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/providers/emedny_visits':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/companies/search_services':
    { UNKNOWN: false, USER: false, MANAGER: false, ADMIN: true },
  '/settings/change_password':
    { UNKNOWN: false, USER: false, MANAGER: true, ADMIN: true },
};

/* PUBLIC */
export const hasPermission = (pathname) => {
  // Get the current user's user type.
  const userType = dataStorage.getUserType() || 'UNKNOWN';

  // Check permission in the permission matrix.
  return !!PERMISSIONS[pathname] && PERMISSIONS[pathname][userType];
};

const Permissions = ({ children }) => {
  const [allow, setAllow] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const redirectWhenNoPermission = () => {
    if (location.pathname in PERMISSIONS) {
      // Prefer encodeURIComponent over encodeURI.
      // https://stackoverflow.com/questions/332872/encode-url-in-javascript
      navigate(`/login?from=${
        encodeURIComponent(location.pathname + location.search + location.hash)}`);
    } else {
      navigate(hasPermission('/') ? '/' : '/login');
    }
  };

  React.useEffect(() => {
    if (hasPermission(location.pathname)) {
      setAllow(true);
    } else {
      setAllow(false);
      redirectWhenNoPermission();
    }
  }, [location]);

  return allow ? children : null;
};

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
Permissions.propTypes = propTypes;

export default Permissions;
